import { Container, Stack, styled, useTheme } from '@mui/material';
import { FilterParams, UserRole } from '@synergy/models';
import { HamburgerIcon, ShoppingCartIcon, SingleInquiryIcon } from '@synergy/theme-next';
import { MouseEvent, ReactElement, useState } from 'react';
import { PoolAvatar, PoolBadge, PoolIconButton, PoolIconButtonProps } from '@synergy/components-next';
import { PoolInstance, isInstance } from '@synergy/config';
import { useNavigate } from 'react-router';

import { AccessControl } from '../../access-control/access-control';
import { CategoryTreeMenu } from '../category-tree-view/menu/category-tree-menu';
import { PlaceLabel } from '../../place-label/place-label';
import { PoolAlpinShopLogo } from '../../pool-alpin-shop-logo/pool-alpin-shop-logo';
import { ROUTES } from '../../../utils/routes';
import { shoppingCartMinimalInfoSelector } from '../../../store/shopping-cart-selectors';
import { useAppSelector } from '../../../../store/store';
import { userSelector } from '../../../store/configuration-selectors';

const StyledContainer = styled(Container)(({ theme }) => ({
    ...theme.typography['subtitle2'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
}));

const StyledPoolIconButton = styled(PoolIconButton)<PoolIconButtonProps & { highlight?: boolean }>(
    ({ theme, highlight }) => ({
        height: highlight ? 40 : undefined,
        backgroundColor: highlight ? theme.palette.primary.main : undefined,
        ':hover': {
            backgroundColor: highlight ? theme.palette.primary.dark : undefined,
        },
        '& .MuiTouchRipple-child': {
            backgroundColor: highlight ? theme.palette.primary.main : theme.palette.grey[800],
        },
    })
);

export const TopNavigationMobile = ({
    onSingleInquiryClick,
    onAccountClick,
}: {
    onSingleInquiryClick: () => void;
    onAccountClick: (e: MouseEvent<HTMLButtonElement>) => void;
}): ReactElement | null => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [showCategories, setShowCategories] = useState(false);
    const user = useAppSelector(userSelector);
    const shoppingCartInfos = useAppSelector(shoppingCartMinimalInfoSelector);

    const handleSelectCategory = (categoryId: number): void => {
        setShowCategories(false);
        navigate({
            pathname: ROUTES.searchPage,
            search: new URLSearchParams({ [FilterParams.CategoryId]: categoryId.toString() }).toString(),
        });
    };

    if (!user) {
        return null;
    }

    return (
        <StyledContainer>
            <Stack direction="row" gap={1}>
                <StyledPoolIconButton size="large" onClick={() => setShowCategories(true)}>
                    <HamburgerIcon fontSize="medium" sx={{ color: theme.palette.common.white }} />
                </StyledPoolIconButton>
                <CategoryTreeMenu
                    isOpen={showCategories}
                    onClose={() => setShowCategories(false)}
                    onSelect={handleSelectCategory}
                />
                <Stack direction="row" alignItems="center">
                    <PoolAlpinShopLogo />
                    <PlaceLabel />
                </Stack>
            </Stack>
            <AccessControl user={user} allowedRoles={[UserRole.ShopAdmin, UserRole.ShopUser]}>
                <Stack direction="row" height="100%" alignItems="center">
                    <StyledPoolIconButton
                        highlight={isInstance(PoolInstance.CH)}
                        data-testid="single-inquiry-button"
                        size="medium"
                        onClick={onSingleInquiryClick}
                    >
                        <SingleInquiryIcon fontSize="medium" sx={{ color: theme.palette.common.white }} />
                    </StyledPoolIconButton>
                    {user && (
                        <StyledPoolIconButton size="large" sx={{ width: 48 }} onClick={onAccountClick}>
                            <PoolAvatar firstName={user.firstName} lastName={user.lastName} />
                        </StyledPoolIconButton>
                    )}
                    <StyledPoolIconButton size="large" onClick={() => navigate(ROUTES.cart.base)}>
                        <PoolBadge badgeContent={shoppingCartInfos.itemCount}>
                            <ShoppingCartIcon fontSize="medium" sx={{ color: theme.palette.common.white }} />
                        </PoolBadge>
                    </StyledPoolIconButton>
                </Stack>
            </AccessControl>
        </StyledContainer>
    );
};
